// NSW Design System JavaScript Module
// Import NSW Design System core functionality
import { initSite, Dialog, Popover, Tabs } from 'nsw-design-system/src/main.js';

/**
 * Initialize NSW Design System
 *
 * This function initializes the NSW Design System which handles:
 * - Navigation (main nav, sub-nav, mobile menu)
 * - Search (header search toggle and focus management)
 * - Tabs, Accordions, Carousels, Dialogs, Popovers
 * - Global Alert, Breadcrumbs, Date Pickers, File Upload
 * - And all other NSWDS components
 */
export function initNSWDesignSystem() {
    console.log('Initializing NSW Design System...');

    // Ensure NSW namespace exists and expose component classes
    // Required for cookie consent and other components that need direct class access
    if (!window.NSW) {
        window.NSW = {};
    }
    window.NSW.Dialog = Dialog;
    window.NSW.Tabs = Tabs;

    // Initialize core NSW components
    // This handles all NSWDS functionality including Navigation, Search, Tabs, etc.
    if (typeof initSite === 'function') {
        initSite();
        console.log('NSW Design System core initialized');
    }

    // Reveal global alerts that haven't been dismissed (prevents FOUC)
    // The template renders with hidden attribute; we show only if no dismiss cookie exists
    revealGlobalAlerts();

    // Custom augmentation: Handle hash-based tab activation
    // This extends NSWDS Tabs to support URL hash navigation (e.g., #tab-name-1)
    handleTabHashActivation();

    console.log('NSW Design System initialization complete');
}

/**
 * Reveal global alert banners that haven't been dismissed
 *
 * The GlobalAlert template renders with the hidden attribute to prevent FOUC.
 * After initSite() runs (which sets up NSWDS GlobalAlert controls), we check
 * the dismiss cookie and reveal the banner only if the user hasn't dismissed it.
 */
function revealGlobalAlerts() {
    const alerts = document.querySelectorAll('.js-global-alert[data-cookie-name]');

    alerts.forEach(function(alert) {
        const cookieName = alert.getAttribute('data-cookie-name');

        if (!cookieName) return;

        // Check if the dismiss cookie exists
        const isDismissed = document.cookie.indexOf(cookieName + '=') !== -1;

        if (!isDismissed) {
            alert.hidden = false;
        }
    });
}

/**
 * Handle hash-based tab activation for preview links
 * This allows tabs to be activated via URL hash (e.g., #tab-name-1)
 */
function handleTabHashActivation() {
    // Activate tab based on current hash
    const activateTabFromHash = function() {
        const hash = window.location.hash;
        if (!hash) return;

        const tabContainers = document.querySelectorAll('.js-tabs');
        tabContainers.forEach(function(container) {
            const tabLinks = container.querySelectorAll('.nsw-tabs__list a');
            tabLinks.forEach(function(link) {
                if (link.getAttribute('href') === hash) {
                    // Click the tab to activate it
                    link.click();

                    // Scroll to the tab container
                    setTimeout(function() {
                        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            });
        });
    };

    // Activate on page load if hash exists
    if (window.location.hash) {
        // Wait for NSWDS to finish initialization
        setTimeout(activateTabFromHash, 100);
    }

    // Handle hash changes (when user clicks CMS preview with different tab)
    window.addEventListener('hashchange', activateTabFromHash);

    console.log('Tab hash activation handlers registered');
}

/**
 * Reinitialize specific NSWDS components after dynamic content changes.
 *
 * Called by the Vue app's `updated` hook so that lazy-loaded async components
 * (popovers, etc.) are properly bound by NSWDS JavaScript once their DOM
 * elements appear. Debounced internally to avoid redundant work when Vue
 * triggers multiple rapid updates.
 *
 * IMPORTANT: We only initialise components that appear inside Vue templates.
 * Calling initSite() again would double-bind Navigation, Accordions, etc.
 * that are already initialised on page load, causing toggle inversions.
 * New components are guarded with a data attribute to prevent re-binding.
 */
let _reinitTimer = null;
const INIT_FLAG = 'data-nsw-init';

export function reinitNSWComponents() {
    clearTimeout(_reinitTimer);
    _reinitTimer = setTimeout(() => {
        document.querySelectorAll(`.js-popover:not([${INIT_FLAG}])`).forEach((el) => {
            new Popover(el).init();
            el.setAttribute(INIT_FLAG, '');
        });
    }, 150);
}